import { useRef, type KeyboardEvent, type ReactNode } from 'react'

export type TileState = 'loading' | 'refreshing' | 'failed' | 'partial' | 'stale'

export type Tile = {
  key: string
  label: string
  /** the counter; null — not known (loading or failed without data) */
  value: ReactNode | null
  /** plain-text counter for screen readers when `value` is markup */
  valueText?: string
  /** one short line under the label */
  meta?: ReactNode
  state?: TileState
}

const STATE_TONE: Record<TileState, string> = {
  loading: 'text-text-3',
  refreshing: 'text-text-3',
  failed: 'text-danger',
  partial: 'text-warn',
  stale: 'text-text-3',
}

/** "failed" with cached data means the refresh failed — the number is still the cached one. */
const stateWord = (t: Tile) => (t.state === 'failed' && t.value !== null ? 'refresh failed' : t.state ?? '')

/**
 * Row of counter tiles that work as tabs: the selected tile has an accent bar at the
 * bottom. Section state (loading, failed, partial, stale) is written on the tile.
 * ←/→/Home/End switch tiles.
 */
export function StatTiles({ tiles, active, onSelect, label, idPrefix, panelId }: {
  tiles: Tile[]
  active: string
  onSelect: (key: string) => void
  /** accessible name of the tab list */
  label: string
  /** tab ids are `${idPrefix}-${key}` */
  idPrefix: string
  /** id of the tab panel the tiles control */
  panelId: string
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = tiles.findIndex((t) => t.key === active)
    let j: number | null = null
    if (e.key === 'ArrowRight') j = (i + 1) % tiles.length
    else if (e.key === 'ArrowLeft') j = (i - 1 + tiles.length) % tiles.length
    else if (e.key === 'Home') j = 0
    else if (e.key === 'End') j = tiles.length - 1
    if (j === null) return
    e.preventDefault()
    const k = tiles[j].key
    onSelect(k)
    refs.current[k]?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className="grid shrink-0 border-b border-line"
      style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}
    >
      {tiles.map((t) => {
        const selected = t.key === active
        const text = t.value === null
          ? (t.state === 'failed' ? 'not loaded' : 'loading')
          : t.valueText ?? (typeof t.value === 'string' || typeof t.value === 'number' ? String(t.value) : '')
        const extra = t.state && t.value !== null ? `, ${stateWord(t)}` : ''
        return (
          <button
            key={t.key}
            ref={(el) => { refs.current[t.key] = el }}
            id={`${idPrefix}-${t.key}`}
            role="tab"
            type="button"
            aria-selected={selected}
            aria-controls={panelId}
            aria-label={`${t.label}, ${text}${extra}`}
            aria-describedby={t.meta ? `${idPrefix}-${t.key}-meta` : undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(t.key)}
            onKeyDown={onKey}
            className={[
              'relative flex min-w-0 flex-col justify-start border-r border-line px-3.5 py-2 text-left transition-colors last:border-r-0 focus-visible:-outline-offset-2',
              selected ? 'bg-tile-active' : 'hover:bg-surface-raised',
            ].join(' ')}
          >
            <div className="flex h-6 items-baseline pr-16">
              {t.value === null
                ? (t.state === 'failed'
                  ? <span className="font-mono text-[18px] leading-6 text-text-3">—</span>
                  : <span aria-hidden className="skeleton inline-block h-4 w-8 self-center" />)
                : <span className="truncate font-mono text-[18px] leading-6 text-foreground">{t.value}</span>}
            </div>
            {t.state && (t.value !== null || t.state !== 'loading') && (
              <span aria-hidden className={`absolute right-3 top-2 text-[11px] font-medium ${STATE_TONE[t.state]}`}>
                {stateWord(t)}
              </span>
            )}
            <div className={`truncate text-[12.5px] ${selected ? 'text-foreground' : 'text-text-2'}`}>{t.label}</div>
            {t.meta && <div id={`${idPrefix}-${t.key}-meta`} className="truncate text-[11px] text-text-3">{t.meta}</div>}
            {selected && <span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" />}
          </button>
        )
      })}
    </div>
  )
}
